// ================================
// Права доступа системы
// ================================

import { UserRole } from './roles';

/**
 * Enum прав доступа
 */
export enum Permission {
    // 🔐 Административные права
    SYSTEM_MANAGE = 'system.manage',
    USER_MANAGE = 'user.manage',
    ROLES_MANAGE = 'roles.manage',

    // 👥 Управление пользователями
    USER_VIEW = 'user.view',
    USER_CREATE = 'user.create',
    USER_EDIT = 'user.edit',
    USER_DELETE = 'user.delete',

    // 📦 Управление товарами
    PRODUCT_VIEW = 'product.view',
    PRODUCT_CREATE = 'product.create',
    PRODUCT_EDIT = 'product.edit',
    PRODUCT_DELETE = 'product.delete',
    PRODUCT_DOWNLOAD = 'product.download',
    PRODUCT_UPLOAD = 'product.upload',
    PRODUCT_MANAGE_INVENTORY = 'product.manage_inventory',
    PRODUCT_MANAGE_TAGS = 'product.manage_tags',

    // Установка цен
    PRICE_FORMULAS = 'price.formulas',

    // Фиды
    FEED_GENERATION = 'feed.generation',

    // 💼 Бизнес-процессы
    ORDERS_MANAGE = 'orders.manage',
    ORDERS_VIEW = 'orders.view',
    ORDERS_CREATE = 'orders.create',
    ORDERS_EDIT = 'orders.edit',

    // 📊 Аналитика и отчеты
    ANALYTICS_VIEW = 'analytics.view',
    REPORTS_GENERATE = 'reports.generate',
    DASHBOARD_VIEW = 'dashboard.view',

    // ⚙️ Технические права
    SETTINGS_MANAGE = 'settings.manage',
    LOGS_VIEW = 'logs.view',
    API_ACCESS = 'api.access'
}

/**
 * Права доступа для каждой роли
 */
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
    [UserRole.ADMIN]: [
        // Все права
        ...Object.values(Permission)
    ],

    [UserRole.DIRECTOR]: [
        // Бизнес-права
        Permission.DASHBOARD_VIEW,
        Permission.ANALYTICS_VIEW,
        Permission.REPORTS_GENERATE,
        Permission.ORDERS_VIEW,
        Permission.ORDERS_MANAGE,

        // Управление
        Permission.USER_VIEW,
        Permission.PRODUCT_VIEW,
        Permission.SETTINGS_MANAGE
    ],

    [UserRole.MANAGER]: [
        // Управление товарами
        Permission.PRODUCT_VIEW,
        Permission.PRODUCT_CREATE,

        // Заказы
        Permission.ORDERS_VIEW,
        Permission.ORDERS_MANAGE,
        Permission.ORDERS_CREATE,
        Permission.ORDERS_EDIT,

        // Аналитика
        Permission.DASHBOARD_VIEW,
        Permission.ANALYTICS_VIEW
    ],

    [UserRole.SUPPORT]: [
        // Технические права
        Permission.USER_VIEW,
        Permission.ORDERS_VIEW,
        Permission.LOGS_VIEW,
        Permission.API_ACCESS,

        // Ограниченное редактирование
        Permission.USER_EDIT,
        Permission.ORDERS_EDIT
    ],

    [UserRole.DOWNLOAD_PRODUCTS]: [
        // Работа с товарами
        Permission.PRODUCT_VIEW,
        Permission.PRODUCT_DOWNLOAD,
        Permission.PRODUCT_UPLOAD,

        // Базовые права
        Permission.DASHBOARD_VIEW
    ],

    [UserRole.USER]: [
        // Базовые права пользователя
        Permission.PRODUCT_VIEW,
        Permission.ORDERS_VIEW,
        Permission.DASHBOARD_VIEW
    ]
};

/**
 * Группы прав для удобных проверок
 */
export const PERMISSION_GROUPS = {
    // Администрирование системы
    ADMINISTRATION: [
        Permission.SYSTEM_MANAGE,
        Permission.USER_MANAGE,
        Permission.ROLES_MANAGE,
        Permission.SETTINGS_MANAGE,
        Permission.LOGS_VIEW
    ],

    // Управление товарами
    PRODUCT_MANAGEMENT: [
        Permission.PRODUCT_VIEW,
        Permission.PRODUCT_CREATE,
        Permission.PRODUCT_EDIT,
        Permission.PRODUCT_DELETE,
        Permission.PRODUCT_DOWNLOAD,
        Permission.PRODUCT_UPLOAD,
        Permission.PRODUCT_MANAGE_INVENTORY,
        Permission.PRODUCT_MANAGE_TAGS
    ],

    // Работа с фидами
    FEEDS_MANAGEMENT: [
      Permission.FEED_GENERATION,
    ],

    // Работа с фидами
    PRICE_MANAGEMENT: [
        Permission.PRICE_FORMULAS,
    ],

    // Работа с заказами
    ORDER_MANAGEMENT: [
        Permission.ORDERS_VIEW,
        Permission.ORDERS_CREATE,
        Permission.ORDERS_EDIT,
        Permission.ORDERS_MANAGE
    ],

    // Аналитика и отчеты
    ANALYTICS: [
        Permission.ANALYTICS_VIEW,
        Permission.REPORTS_GENERATE,
        Permission.DASHBOARD_VIEW
    ],

    // Технические права
    TECHNICAL: [
        Permission.LOGS_VIEW,
        Permission.API_ACCESS
    ]
} as const;

// Хелпер функции
/**
 * Проверить, есть ли у роли указанное право
 */
export const roleHasPermission = (roleId: UserRole, permission: Permission): boolean => {
    const rolePermissions = ROLE_PERMISSIONS[roleId] || [];
    return rolePermissions.includes(permission);
};

/**
 * Получить все права для роли
 */
export const getRolePermissions = (roleId: UserRole): Permission[] => {
    return ROLE_PERMISSIONS[roleId] || [];
};

/**
 * Проверить, является ли право валидным
 */
export const isValidPermission = (permission: string): permission is Permission => {
    return Object.values(Permission).includes(permission as Permission);
};

/**
 * Получить роли, которые имеют указанное право
 */
export const getRolesWithPermission = (permission: Permission): UserRole[] => {
    return Object.entries(ROLE_PERMISSIONS)
        .filter(([_, permissions]) => permissions.includes(permission))
        .map(([roleId]) => parseInt(roleId) as UserRole);
};